import { useEffect, type MouseEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { Badge, cn, NavigationDrawer } from '@crm/ui';
import { useTenant } from '../tenant/TenantProvider.tsx';
import { visibleTabs } from '../admin/tabs.ts';
import { ADMIN_NAV, MAIN_NAV, allowedNav, type NavItem } from './nav.ts';

function Item({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const Icon = item.icon;
  const base = 'flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white';
  if (item.soon) {
    return (
      <span className={cn(base, 'cursor-not-allowed text-white/45')} title={t('app.soon')} aria-disabled>
        <Icon size={18} aria-hidden />
        <span className="flex-1">{t(item.labelKey)}</span>
        <Badge className="bg-white/10 text-white/70">{t('app.soon')}</Badge>
      </span>
    );
  }
  const openModule = (event: MouseEvent<HTMLAnchorElement>) => {
    // Les routes sont chargées à la demande. Sans commit synchrone, React peut garder l'ancien
    // module monté pendant le téléchargement ; un effet tardif de ce module (filtres URL) peut
    // alors annuler le clic. On démonte d'abord l'ancien écran, puis le fallback prend le relais.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(item.to, { flushSync: true });
  };
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={openModule}
      data-testid={`nav-${item.to.replace(/\W+/g, '') || 'home'}`}
      className={({ isActive }) =>
        cn(base, isActive ? 'bg-white text-brand shadow-sm' : 'text-white/75 hover:bg-white/10 hover:text-white')
      }
    >
      <Icon size={18} aria-hidden />
      {t(item.labelKey)}
    </NavLink>
  );
}

export function Sidebar({ mobileOpen, onClose, menuButtonRef }: { mobileOpen: boolean; onClose: () => void; menuButtonRef: RefObject<HTMLButtonElement | null> }) {
  const { t } = useTranslation();
  const { can, context } = useTenant();
  const location = useLocation();
  const hasAdmin = visibleTabs(can, context.scopes.length === 0).length > 0;
  const admin = ADMIN_NAV.filter((i) => (i.permission === 'admin' ? hasAdmin : !i.permission || can(i.permission)));
  const visible = allowedNav(MAIN_NAV, can);
  const groups = [
    { key: 'commercial', paths: ['/', '/comptes', '/pipeline', '/visites', '/ventes', '/encaissements'] },
    { key: 'operations', paths: ['/stock', '/catalogue', '/logistique', '/reclamations', '/elevage', '/meunerie', '/qualite'] },
    { key: 'management', paths: ['/objectifs', '/analyses', '/import'] },
  ];

  // Une entrée d'historique n'est créée qu'après une navigation acceptée : le menu
  // se ferme aussi pour la même route ou ses paramètres, sans masquer un refus de quitter.
  useEffect(() => onClose(), [location.key, onClose]);
  useEffect(() => {
    if (!mobileOpen || !window.matchMedia) return;
    const desktop = window.matchMedia('(min-width: 768px)');
    const closeOnDesktop = () => { if (desktop.matches) onClose(); };
    closeOnDesktop();
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, [mobileOpen, onClose]);

  const contents = <>
    <nav className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 py-4" aria-label={t('nav.menu')}>
      {groups.map((group) => { const items = group.paths.flatMap((path) => visible.filter((item) => item.to === path)); return items.length ? <div key={group.key} className="space-y-1"><p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[.15em] text-white/70">{t(`workspace.groups.${group.key}`)}</p>{items.map((item) => <Item key={item.to} item={item} />)}</div> : null; })}
      {admin.length ? <div className="space-y-1 border-t border-white/10 pt-3">{admin.map((item) => <Item key={item.to} item={item} />)}</div> : null}
    </nav>
    <p className="px-4 py-3 text-xs text-white/70">v{import.meta.env.VITE_APP_VERSION ?? '0.1.0'}</p>
  </>;
  const title = context.tenant.theme.product_name || 'CRM Agro';

  return (
    <>
      <aside className="hidden w-60 shrink-0 flex-col border-r border-white/10 bg-brand text-white md:flex" aria-label={t('nav.menu')}>
        <div className="flex h-[72px] shrink-0 items-center border-b border-white/10 px-5">
          <span className="flex items-center gap-3 truncate font-heading text-lg font-semibold"><span className="h-6 w-1 shrink-0 rounded-full bg-accent" aria-hidden />{title}</span>
        </div>
        {contents}
      </aside>
      <NavigationDrawer open={mobileOpen} onOpenChange={(open) => { if (!open) onClose(); }} title={title} closeLabel={t('workspace.closeMenu')} returnFocusRef={menuButtonRef}>{contents}</NavigationDrawer>
    </>
  );
}
